import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../../utils/reportsApi';
import { api } from '../../utils/api';
import './ReportCommon.css';

function fmt(n) {
  const v = Number(n);
  if (!v) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtNet(n) {
  const v = Number(n);
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const PnlPage = () => {
  const navigate = useNavigate();

  const [mode,         setMode]         = useState('period'); // 'period' | 'year'
  const [periodId,     setPeriodId]     = useState('');
  const [fiscalYearId, setFiscalYearId] = useState('');
  const [entityId,     setEntityId]     = useState('');

  const [periods,      setPeriods]      = useState([]);
  const [fiscalYears,  setFiscalYears]  = useState([]);
  const [entities,     setEntities]     = useState([]);
  const [result,       setResult]       = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [exporting,    setExporting]    = useState(false);
  const [error,        setError]        = useState(null);

  useEffect(() => {
    Promise.all([api.get('/fiscal/fiscal-years'), api.get('/entities')]).then(([fys, ents]) => {
      setFiscalYears(fys || []);
      const allPeriods = (fys || []).flatMap((fy) =>
        (fy.periods || []).map((p) => ({ ...p, fyCode: fy.code }))
      );
      allPeriods.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
      setPeriods(allPeriods);
      setEntities(ents || []);
    }).catch(() => {});
  }, []);

  const params = mode === 'period'
    ? { periodId: periodId || undefined, entityId: entityId || undefined }
    : { fiscalYearId: fiscalYearId || undefined, entityId: entityId || undefined };

  const run = async () => {
    if (mode === 'period' && !periodId) { setError('Select a period.'); return; }
    if (mode === 'year'   && !fiscalYearId) { setError('Select a fiscal year.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      setResult(await reportsApi.getPnl(params));
    } catch (e) {
      setError(e.message || 'Failed to run report');
    } finally { setLoading(false); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try { await reportsApi.exportPnl(params, 'pnl-report.xlsx'); }
    catch (e) { setError(e.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  const incomeLines  = result?.income?.lines  ?? [];
  const expenseLines = result?.expenses?.lines ?? [];

  return (
    <div className="rpt-page">
      <div className="rpt-header">
        <button className="rpt-back" onClick={() => navigate('/reports')}><i className="fas fa-arrow-left"></i> Reports</button>
        <h1 className="rpt-title">Revenues &amp; Expenses (P&amp;L)</h1>
      </div>

      <div className="rpt-filters">
        <label className="rpt-label">
          Scope
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="rpt-select">
            <option value="period">Single Period</option>
            <option value="year">Full Fiscal Year</option>
          </select>
        </label>
        {mode === 'period' ? (
          <label className="rpt-label">
            Period <span className="rpt-req">*</span>
            <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} className="rpt-select">
              <option value="">— select period —</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.fyCode} · {p.name}</option>)}
            </select>
          </label>
        ) : (
          <label className="rpt-label">
            Fiscal Year <span className="rpt-req">*</span>
            <select value={fiscalYearId} onChange={(e) => setFiscalYearId(e.target.value)} className="rpt-select">
              <option value="">— select year —</option>
              {fiscalYears.map((fy) => <option key={fy.id} value={fy.id}>{fy.name}</option>)}
            </select>
          </label>
        )}
        <label className="rpt-label">
          Entity
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="rpt-select">
            <option value="">All entities</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <div className="rpt-filter-actions">
          <button className="rpt-btn rpt-btn--primary" onClick={run} disabled={loading}>
            {loading ? <><i className="fas fa-spinner fa-spin"></i> Running…</> : <><i className="fas fa-play"></i> Run</>}
          </button>
          {result && (
            <button className="rpt-btn rpt-btn--excel" onClick={exportXlsx} disabled={exporting}>
              {exporting ? <><i className="fas fa-spinner fa-spin"></i> Exporting…</> : <><i className="fas fa-file-excel"></i> Export Excel</>}
            </button>
          )}
        </div>
      </div>

      {error && <div className="rpt-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {result && (
        <div className="rpt-result">
          <div className="rpt-meta">
            {result.period?.name ?? result.fiscalYear?.name}
            {result.entity && <> &nbsp;·&nbsp; <strong>{result.entity.name}</strong></>}
          </div>

          <div className="rpt-table-wrap">
            <table className="rpt-table">
              <thead>
                <tr>
                  <th className="col-code">Code</th>
                  <th className="col-name">Account</th>
                  <th className="col-amt">Dr (EGP)</th>
                  <th className="col-amt">Cr (EGP)</th>
                  <th className="col-amt">Net</th>
                </tr>
              </thead>
              <tbody>
                {/* Income */}
                <tr className="row-section-header">
                  <td colSpan="5"><i className="fas fa-arrow-trend-up" style={{ marginRight: 8, color: '#166534' }}></i>INCOME</td>
                </tr>
                {incomeLines.length === 0 && (
                  <tr><td colSpan="5" className="rpt-empty">No income activity.</td></tr>
                )}
                {incomeLines.map((r) => (
                  <tr key={r.accountId}>
                    <td className="col-code">{r.code}</td>
                    <td className="col-name">{r.name}</td>
                    <td className="col-amt">{fmt(r.movementDebit)}</td>
                    <td className="col-amt">{fmt(r.movementCredit)}</td>
                    <td className="col-amt" style={{ color: '#166534', fontWeight: 600 }}>{fmtNet(r.net)}</td>
                  </tr>
                ))}
                <tr className="row-section-total">
                  <td colSpan="4" style={{ fontWeight: 700 }}>Total Income</td>
                  <td className="col-amt" style={{ color: '#166534', fontWeight: 700 }}>{fmtNet(result.income.total)}</td>
                </tr>

                {/* spacer */}
                <tr><td colSpan="5" style={{ height: 8 }}></td></tr>

                {/* Expenses */}
                <tr className="row-section-header">
                  <td colSpan="5"><i className="fas fa-arrow-trend-down" style={{ marginRight: 8, color: '#9a3412' }}></i>EXPENSES</td>
                </tr>
                {expenseLines.length === 0 && (
                  <tr><td colSpan="5" className="rpt-empty">No expense activity.</td></tr>
                )}
                {expenseLines.map((r) => (
                  <tr key={r.accountId}>
                    <td className="col-code">{r.code}</td>
                    <td className="col-name">{r.name}</td>
                    <td className="col-amt">{fmt(r.movementDebit)}</td>
                    <td className="col-amt">{fmt(r.movementCredit)}</td>
                    <td className="col-amt" style={{ color: '#9a3412', fontWeight: 600 }}>{fmtNet(r.net)}</td>
                  </tr>
                ))}
                <tr className="row-section-total">
                  <td colSpan="4" style={{ fontWeight: 700 }}>Total Expenses</td>
                  <td className="col-amt" style={{ color: '#9a3412', fontWeight: 700 }}>{fmtNet(result.expenses.total)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="row-net">
                  <td colSpan="4">NET INCOME</td>
                  <td className="col-amt" style={{ color: result.netIncome >= 0 ? '#166534' : '#9a3412' }}>
                    {fmtNet(result.netIncome)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="rpt-summary">
            <div className="rpt-summary-card rpt-summary-card--green">
              <div className="rpt-summary-card__label">Total Income</div>
              <div className="rpt-summary-card__value">{fmtNet(result.income.total)}</div>
            </div>
            <div className="rpt-summary-card rpt-summary-card--red">
              <div className="rpt-summary-card__label">Total Expenses</div>
              <div className="rpt-summary-card__value">{fmtNet(result.expenses.total)}</div>
            </div>
            <div className={`rpt-summary-card ${result.netIncome >= 0 ? 'rpt-summary-card--green' : 'rpt-summary-card--red'}`}>
              <div className="rpt-summary-card__label">Net Income</div>
              <div className="rpt-summary-card__value">{fmtNet(result.netIncome)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PnlPage;
